import type { TailoredpayTokenizeResult } from '../constants';

/**
 * Collect.js wrapper.
 *
 * Collect.js is callback-based and effectively configure-once-per-page: the
 * response callback is registered at `configure()` time, not per submit. So we
 * configure once, and every `tokenize()` call parks a deferred that the shared
 * callback resolves. That mirrors how the Recurly branch in CardForm wraps
 * `payment.token()` in a promise.
 */

type CollectFieldSelectors = {
  ccnumber: string;
  ccexp: string;
  cvv: string;
};

type ConfigureParams = {
  selectors: CollectFieldSelectors;
  placeholders?: Partial<Record<keyof CollectFieldSelectors, string>>;
  /** Fires on every field validation change — used for inline error styling. */
  onValidation?: (field: string, isValid: boolean, message: string) => void;
  onFieldsAvailable?: () => void;
  /** Collect.js gives up on tokenization after this many ms. */
  timeoutMs?: number;
  /**
   * Amount charged today / its currency / merchant country. Required even though
   * we never render a wallet button: Collect.js builds its digital-wallet
   * abstraction during configure() whenever the gateway account has Apple Pay
   * turned on, and without these it throws "Could not create
   * PaymentRequestAbstraction".
   */
  price?: string | number;
  currency?: string;
  country?: string;
  /**
   * Collect.js renders its own Apple Pay button into this field's selector — see
   * ./apple-pay. Omitted entirely when the gateway has no Apple Pay, so the SDK
   * never tries to build a wallet button we don't want.
   */
  applePay?: Record<string, any> | null;
  /**
   * Called when a wallet payment (Apple Pay) is tokenized. Wallet payments are
   * started by the SDK's own button rather than by our submit handler, so they
   * arrive without anything waiting on them.
   */
  onWalletToken?: (result: TailoredpayTokenizeResult) => void;
};

const DEFAULT_TIMEOUT_MS = 15000;

type Deferred = {
  resolve: (value: TailoredpayTokenizeResult) => void;
  reject: (reason: Error) => void;
};

let pending: Deferred | null = null;
let configuredSignature: string | null = null;
let walletTokenHandler: ((result: TailoredpayTokenizeResult) => void) | null = null;

/**
 * Our own fault, not the cardholder's: the SDK never loaded or was never
 * configured. Callers use the flag to fall back to another payment system
 * silently instead of telling the user to check their card.
 */
const configurationError = (message: string) =>
  Object.assign(new Error(message), { isConfigurationError: true });

/** Aborted by us (modal closed, superseded submit) — must surface nothing. */
const silentError = (message: string) =>
  Object.assign(new Error(message), { isSilent: true });

const settle = (fn: (d: Deferred) => void) => {
  const deferred = pending;
  pending = null;
  if (deferred) {
    fn(deferred);
  }
};

const getCollectJs = () => (window as any).CollectJS;

export const isCollectJsReady = () => Boolean(getCollectJs());

const READY_POLL_INTERVAL_MS = 50;
const READY_TIMEOUT_MS = 10000;

/**
 * Collect.js is injected asynchronously during billing init, so a card form that
 * mounts early would otherwise find `window.CollectJS` missing and give up. Wait
 * for the global instead of failing on first render.
 */
export const waitForCollectJs = (timeoutMs = READY_TIMEOUT_MS): Promise<void> => {
  if (getCollectJs()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;

    const intervalId = window.setInterval(() => {
      if (getCollectJs()) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() > deadline) {
        window.clearInterval(intervalId);
        reject(configurationError('[TailoredPay] Collect.js did not load in time'));
      }
    }, READY_POLL_INTERVAL_MS);
  });
};

/**
 * Reset the memoized configuration. Needed when the payment modal unmounts and
 * remounts: the old iframes are gone with the DOM nodes, so Collect.js has to be
 * pointed at the fresh containers.
 */
export const resetCollectJsConfiguration = () => {
  configuredSignature = null;
  walletTokenHandler = null;
  settle((d) => d.reject(silentError('Card form was closed')));
};

export const configureCollectJs = ({
  selectors,
  placeholders,
  onValidation,
  onFieldsAvailable,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  price,
  currency,
  country = 'US',
  applePay,
  onWalletToken,
}: ConfigureParams) => {
  const CollectJS = getCollectJs();

  if (!CollectJS) {
    throw new Error('[TailoredPay] Collect.js is not loaded');
  }

  // The price is part of the signature: a plan change has to reach the wallet
  // abstraction, otherwise it would keep quoting the previous amount.
  const signature = JSON.stringify({ selectors, price, currency, country, applePay });

  // Re-configuring against the same selectors would tear down working iframes
  // and blank the user's input, so skip it.
  if (configuredSignature === signature) {
    return;
  }

  // Kept outside the config so the wallet callback can find it: `configure()` is
  // what registers the shared response callback, and the handler has to be
  // reachable from there.
  walletTokenHandler = onWalletToken || null;

  CollectJS.configure({
    variant: 'inline',
    ...(price != null ? { price: String(price) } : {}),
    ...(currency ? { currency } : {}),
    ...(country ? { country } : {}),
    // Let the host page own the look — Collect.js sniffs sibling input styles
    // and copies them into its iframes.
    styleSniffer: true,
    // NOTE: Collect.js validates this payload against a fixed field list and
    // throws "Unexpected fields" on anything it doesn't know — so only add keys
    // that are actually documented for the gateway's Collect.js build.
    timeoutDuration: timeoutMs,
    timeoutCallback: () => {
      settle((d) => d.reject(new Error('Card tokenization timed out')));
    },
    fieldsAvailableCallback: () => {
      onFieldsAvailable?.();
    },
    validationCallback: (field: string, isValid: boolean, message: string) => {
      onValidation?.(field, isValid, message);
    },
    fields: {
      ccnumber: {
        selector: selectors.ccnumber,
        placeholder: placeholders?.ccnumber ?? '1234 5678 9012 3456',
        // Shows the card brand inside the field as the number is typed. Off by
        // default in Collect.js. It has to be Collect.js doing this: the SDK never
        // exposes the detected brand to the host page (no callback carries it — the
        // brand only arrives with the token, after submit), so the field's own
        // preview is the only way to show it while the user is still typing.
        enableCardBrandPreviews: true,
      },
      ccexp: {
        selector: selectors.ccexp,
        placeholder: placeholders?.ccexp ?? 'MM / YY',
      },
      cvv: {
        selector: selectors.cvv,
        placeholder: placeholders?.cvv ?? 'CVV',
      },
      ...(applePay ? { applePay } : {}),
    },
    callback: (response: any) => {
      // Collect.js labels card tokenizations 'inline' and wallet ones by wallet
      // ('applePay' / 'googlePay'). The two need opposite handling: a card token
      // was asked for by tokenize() and has a deferred waiting on it, a wallet
      // token arrives unprompted from the SDK's own button.
      const isWallet = response?.tokenType === 'applePay' || response?.tokenType === 'googlePay';

      if (!response?.token) {
        if (isWallet) {
          // Nobody is waiting on this one, and the native sheet has already
          // reported the failure to the user.
          console.warn('[TailoredPay] wallet tokenization returned no token');
          return;
        }

        settle((d) => d.reject(new Error('Card tokenization returned no token')));
        return;
      }

      const result = {
        payment_token: response.token,
        tokenType: response.tokenType,
        card: response.card,
        wallet: response.wallet,
      };

      if (isWallet) {
        walletTokenHandler?.(result);
        return;
      }

      settle((d) => d.resolve(result));
    },
  });

  configuredSignature = signature;
};

/**
 * Ask Collect.js for a payment token. The token is SINGLE-USE and expires after
 * 24 hours, so it must be handed straight to the backend — never cached, never
 * reused for a retry.
 */
export const tokenize = (): Promise<TailoredpayTokenizeResult> => {
  const CollectJS = getCollectJs();

  if (!CollectJS) {
    return Promise.reject(configurationError('[TailoredPay] Collect.js is not loaded'));
  }

  if (!configuredSignature) {
    return Promise.reject(configurationError('[TailoredPay] Collect.js is not configured'));
  }

  // A second submit while one is in flight would have its result delivered to
  // the first deferred, so reject the older one explicitly.
  settle((d) => d.reject(silentError('Superseded by a newer submit')));

  return new Promise<TailoredpayTokenizeResult>((resolve, reject) => {
    pending = { resolve, reject };
    try {
      CollectJS.startPaymentRequest();
    } catch (e: any) {
      settle((d) => d.reject(e instanceof Error ? e : new Error(String(e))));
    }
  });
};
